import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import productsData from "../data/products.json";
import LEDGroupedCard from "../Components/LEDGroupedCard";
import MobileGroupedCard from "../Components/MobileGroupedCard";

function Products() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Helper function to map color names to hex codes
  const getColorHex = (colorName) => {
    const colorMap = {
      // Blacks & Grays
      'Black': '#000000',
      'Titanium Black': '#2C2C2E',
      'Titan Black': '#2C2C2E',
      'Titanium Jet Black': '#1C1C1C',
      'Graphite': '#3A3A3C',
      'Graphite Black': '#2C2C2C',
      'Awesome Graphite': '#3A3A3C',
      'Black Caviar': '#1C1C1C',
      'Deep Charcoal': '#36454F',
      'Dark Gray': '#4A4A4A',
      'Gray': '#808080',
      'Titanium Gray': '#6B7280',
      'Titan Gray': '#6B7280',
      'Lavender Gray': '#9CA3AF',
      'Awesome Lightgray': '#D1D5DB',
      'Blue Black': '#1E293B',

      // Whites & Silvers
      'White': '#FFFFFF',
      'Awesome White': '#FAFAFA',
      'Modern White': '#F8F8F8',
      'Silver': '#C0C0C0',
      'Titanium White Silver': '#E5E7EB',

      // Blues
      'Navy': '#1E3A8A',
      'Awesome Navy': '#1E3A8A',
      'Brushed Navy': '#1E40AF',
      'Titanium Silver Blue': '#60A5FA',
      'Light Blue': '#BFDBFE',
      'Iceblue': '#A5F3FC',
      'Awesome Iceblue': '#A5F3FC',

      // Purples & Violets
      'Violet': '#8B5CF6',
      'Awesome Violet': '#8B5CF6',
      'Light Violet': '#C4B5FD',
      'Lilac': '#C4B5FD',
      'Awesome Lilac': '#C4B5FD',
      'Bora Purple': '#A855F7',

      // Greens
      'Green': '#22C55E',
      'Mint': '#86EFAC',
      'Light Green': '#BBF7D0',
      'Titanium Jade Green': '#6EE7B7',
      'Lime': '#BEF264',
      'Awesome Lime': '#BEF264',
      'Awesome Olive': '#84CC16',

      // Yellows & Golds
      'Yellow': '#FCD34D',
      'Lemon': '#FEF08A',
      'Awesome Lemon': '#FEF08A',
      'Gold': '#F59E0B',
      'Pink Gold': '#E8B69A',
      'Titanium Pink Gold': '#E8B69A',
      'Sand Gold Metal': '#D4AF37',

      // Pinks
      'Peach Pink': '#FED7E2',
      'Awesome Pink': '#FCA5A5',

      // Browns & Beiges
      'Beige': '#F5E6D3',
      'Modern Brown': '#8B7355',
      'Modern Teak': '#B8956A',

      // Special Titanium Colors
      'Titanium': '#B4B4B4',
    };

    return colorMap[colorName] || '#CCCCCC';
  };

  // WhatsApp order function
  const handleWhatsAppOrder = (product) => {
    const specs = [];
    if (product.ram) specs.push(`RAM: ${product.ram}`);
    if (product.storage) specs.push(`Storage: ${product.storage}`);
    if (product.size) specs.push(`Size: ${product.size}`);
    if (product.channels) specs.push(product.channels);
    if (product.type) specs.push(product.type);

    const specsText = specs.length > 0 ? `\nSpecs: ${specs.join(', ')}` : '';
    const discountText = product.discount > 0 ? `\nOriginal Price: PKR ${product.originalPrice.toLocaleString()}\nDiscount: ${product.discount}%` : '';

    const message = `Hi *Samsung Door Step*! 👋\n\nI'm interested in:\n\n📱 *${product.name}*${specsText}\n💰 Price: *PKR ${product.price.toLocaleString()}*${discountText}\n\nPlease provide more details.`;

    const whatsappURL = `https://wa.me/923292737737?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
  };

  // Map category keys to display names
  const categoryMapping = {
    mobiles: "Mobiles",
    leds: "LEDs",
    homeAppliances: "Home Appliances",
    watches: "Watches",
    audio: "Audio",
    tablets: "Tablets",
    earbuds: "Earbuds",
    soundbars: "Soundbars",
    projectors: "Projectors",
    accessories: "Accessories",
    wearables: "Wearables"
  };

  // Handle URL parameters for filtering
  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      const displayCategory = categoryMapping[category] || "All";
      setSelectedCategory(displayCategory);

      // Scroll to filtered results section
      setTimeout(() => {
        const filteredSection = document.querySelector('.category-section');
        if (filteredSection) {
          filteredSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [searchParams]);

  // Get all products from all category arrays
  const allProducts = [
    ...productsData.mobiles,
    ...productsData.leds,
    ...productsData.homeAppliances,
    ...productsData.watches,
    ...productsData.audio,
    ...productsData.tablets,
    ...productsData.earbuds,
    ...productsData.soundbars,
    ...productsData.projectors
  ];

  // Filter products based on URL parameters
  const getFilteredProducts = () => {
    const category = searchParams.get('category');
    const series = searchParams.get('series');
    const type = searchParams.get('type');

    let filtered = allProducts;

    // Filter by category
    if (category && category !== 'all') {
      // When filtering mobiles, include tablets as well
      if (category === 'mobiles') {
        filtered = [...productsData.mobiles, ...productsData.tablets];
      }
      // When filtering wearables, include watches and earbuds
      else if (category === 'wearables') {
        filtered = [...productsData.watches, ...productsData.earbuds];
      } else {
        filtered = productsData[category] || allProducts;
      }
    }

    // Filter by series (for mobiles: S series, A series)
    if (series) {
      filtered = filtered.filter(product => {
        const name = product.name.toLowerCase();
        if (series === 's') {
          return name.includes('galaxy s') && !name.includes('galaxy a');
        } else if (series === 'a') {
          return name.includes('galaxy a');
        }
        return true;
      });
    }

    // Filter by type (for TVs: QLED, OLED, etc.)
    if (type) {
      filtered = filtered.filter(product => {
        const name = product.name.toLowerCase();
        if (type === 'qled') {
          return name.includes('qled');
        } else if (type === 'oled') {
          return name.includes('oled');
        } else if (type === 'refrigerator') {
          return name.includes('refrigerator');
        } else if (type === 'washing') {
          return name.includes('washing');
        }
        return true;
      });
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Group LED products by model (e.g., Q80D, Q70D, etc.)
  const groupLEDsByModel = (leds) => {
    const grouped = {};

    leds.forEach(led => {
      // Extract model from series or name
      // Handle special cases like "THE FRAME" and regular models like "Q80D (QLED Ultra Premium)"
      let modelKey;

      if (led.series?.startsWith('THE FRAME')) {
        modelKey = 'THE FRAME';
      } else {
        // Extract model code (e.g., "Q80D", "DU7000", "QN85D")
        const seriesMatch = led.series?.match(/^([A-Z0-9]+)/);
        modelKey = seriesMatch ? seriesMatch[1] : led.name;
      }

      if (!grouped[modelKey]) {
        grouped[modelKey] = {
          model: modelKey,
          series: led.series,
          resolution: led.resolution,
          products: []
        };
      }

      grouped[modelKey].products.push(led);
    });

    // Sort products within each group by size (ascending)
    Object.values(grouped).forEach(group => {
      group.products.sort((a, b) => {
        const sizeA = parseInt(a.size);
        const sizeB = parseInt(b.size);
        return sizeA - sizeB;
      });
    });

    return Object.values(grouped);
  };

  // Group Mobile products by base model name
  const groupMobilesByModel = (mobiles) => {
    const grouped = {};

    mobiles.forEach(mobile => {
      // Extract base model name (without RAM/storage specs)
      const baseName = mobile.name
        .replace(/\s*\d+GB\/\d+GB/g, '')
        .replace(/\s*\(\d+GB\s+RAM\)/g, '')
        .replace(/\s*\d+GB\s+RAM/g, '')
        .replace(/\s*\d+GB/g, '')
        .trim();

      if (!grouped[baseName]) {
        grouped[baseName] = {
          model: baseName,
          variants: []
        };
      }

      grouped[baseName].variants.push(mobile);
    });

    return Object.values(grouped);
  };

  // Get products grouped by category
  const productsByCategory = {
    "All": allProducts,
    "Mobiles": productsData.mobiles,
    "LEDs": productsData.leds,
    "Home Appliances": productsData.homeAppliances,
    "Watches": productsData.watches,
    "Audio": productsData.audio,
    "Tablets": productsData.tablets,
    "Earbuds": productsData.earbuds,
    "Soundbars": productsData.soundbars,
    "Projectors": productsData.projectors,
    "Accessories": productsData.projectors,
    "Wearables": [...productsData.watches, ...productsData.earbuds]
  };

  // Check if we have URL parameters for filtering
  const hasUrlFilters = searchParams.get('category') || searchParams.get('series') || searchParams.get('type');

  // If "All" is selected, show all categories
  // Otherwise, show only the selected category
  const categoriesToShow = selectedCategory === "All"
    ? Object.values(categoryMapping)
    : [selectedCategory];

  return (
    <div className="products-page">
      <div className="page-header">
        <h1>Our Products</h1>
        <p>Discover the complete range of Samsung products available for doorstep delivery</p>
      </div>

      <div className="container">
        {/* Show filtered products if URL parameters exist */}
        {hasUrlFilters ? (
          <section className="category-section">
            <div className="category-title-section">
              <h2>Filtered Results</h2>
              {(() => {
                const category = searchParams.get('category');
                const isLEDCategory = category === 'leds';
                const groupedLEDs = isLEDCategory ? groupLEDsByModel(filteredProducts) : null;

                return isLEDCategory ? (
                  <p className="product-count">{groupedLEDs.length} models</p>
                ) : (
                  <p className="product-count">{filteredProducts.length} products</p>
                );
              })()}
            </div>

            {(() => {
              const category = searchParams.get('category');
              const isLEDCategory = category === 'leds';
              const isMobileCategory = category === 'mobiles';

              // Special handling for LEDs - group by model
              if (isLEDCategory) {
                const groupedLEDs = groupLEDsByModel(filteredProducts);

                return (
                  <div className="products-grid">
                    {groupedLEDs.map((group) => (
                      <LEDGroupedCard
                        key={group.model}
                        group={group}
                        handleWhatsAppOrder={handleWhatsAppOrder}
                      />
                    ))}
                  </div>
                );
              }

              // Special handling for Mobiles - group by model
              if (isMobileCategory) {
                const groupedMobiles = groupMobilesByModel(filteredProducts);

                return (
                  <div className="products-grid">
                    {groupedMobiles.map((group) => (
                      <MobileGroupedCard
                        key={group.model}
                        group={group}
                      />
                    ))}
                  </div>
                );
              }

              // Regular display for other categories
              return (
                <div className="products-grid">
                  {filteredProducts.map((product) => {
                    // Use first color image if available, otherwise use default image
                    const displayImage = product.colorImages && product.colorImages.length > 0
                      ? product.colorImages[0]
                      : product.image;

                    return (
                    <div
                      key={product.id}
                      className="product-card"
                      onClick={() => navigate(`/product/${product.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="product-image">
                        <img src={displayImage} alt={product.name} />
                      </div>

                      <h3>{product.name}</h3>

                      {/* Color Dots - Only show colors that have corresponding images */}
                      {product.colors && product.colors.length > 0 && product.colorImages && (
                        <div className="product-card-colors">
                          {product.colors.slice(0, Math.min(product.colorImages.length, 5)).map((color, index) => (
                            <div
                              key={index}
                              className="color-dot"
                              style={{
                                backgroundColor: getColorHex(color),
                                border: getColorHex(color) === '#FFFFFF' || getColorHex(color) === '#F8F8F8' ? '1.5px solid #ddd' : '1.5px solid rgba(0, 0, 0, 0.15)'
                              }}
                              title={color}
                            />
                          ))}
                          {product.colorImages.length > 5 && (
                            <span style={{ fontSize: '0.75rem', color: '#666' }}>+{product.colorImages.length - 5}</span>
                          )}
                        </div>
                      )}

                      <div className="product-specs">
                        {product.ram && <span className="spec">RAM: {product.ram}</span>}
                        {product.storage && <span className="spec">Storage: {product.storage}</span>}
                        {product.size && <span className="spec">Size: {product.size}</span>}
                        {product.channels && <span className="spec">{product.channels}</span>}
                        {product.type && <span className="spec">{product.type}</span>}
                      </div>

                      <div className="product-price-section">
                        <div className="price-info">
                          <p className="current-price">PKR {product.price.toLocaleString()}</p>
                          {product.discount > 0 && (
                            <p className="original-price">PKR {product.originalPrice.toLocaleString()}</p>
                          )}
                        </div>
                      </div>

                      <button className="btn-add-to-cart" onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsAppOrder(product);
                      }}>
                        Shop Now
                      </button>
                    </div>
                    );
                  })}
                </div>
              );
            })()}
          </section>
        ) : (
          /* Products by Category */
          categoriesToShow.map((category) => {
            const categoryProducts = productsByCategory[category];

            // Special handling for LEDs - group by model
            if (category === "LEDs") {
              const groupedLEDs = groupLEDsByModel(categoryProducts);

              return (
                <section key={category} className="category-section">
                  <div className="category-title-section">
                    <h2>{category}</h2>
                    <p className="product-count">{groupedLEDs.length} models</p>
                  </div>

                  <div className="products-grid">
                    {groupedLEDs.map((group) => (
                      <LEDGroupedCard
                        key={group.model}
                        group={group}
                        handleWhatsAppOrder={handleWhatsAppOrder}
                      />
                    ))}
                  </div>
                </section>
              );
            }

            // Special handling for Mobiles - group by model
            if (category === "Mobiles") {
              const groupedMobiles = groupMobilesByModel(categoryProducts);

              return (
                <section key={category} className="category-section">
                  <div className="category-title-section">
                    <h2>{category}</h2>
                    <p className="product-count">{groupedMobiles.length} models</p>
                  </div>

                  <div className="products-grid">
                    {groupedMobiles.map((group) => (
                      <MobileGroupedCard
                        key={group.model}
                        group={group}
                      />
                    ))}
                  </div>
                </section>
              );
            }

            // Regular display for other categories
            return (
              <section key={category} className="category-section">
                <div className="category-title-section">
                  <h2>{category}</h2>
                  <p className="product-count">{categoryProducts.length} products</p>
                </div>

                <div className="products-grid">
                  {categoryProducts.map((product) => {
                    // Use first color image if available, otherwise use default image
                    const displayImage = product.colorImages && product.colorImages.length > 0
                      ? product.colorImages[0]
                      : product.image;

                    return (
                  <div
                    key={product.id}
                    className="product-card"
                    onClick={() => navigate(`/product/${product.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="product-image">
                      <img src={displayImage} alt={product.name} />
                    </div>

                    <h3>{product.name}</h3>

                    {/* Color Dots - Only show colors that have corresponding images */}
                    {product.colors && product.colors.length > 0 && product.colorImages && (
                      <div className="product-card-colors">
                        {product.colors.slice(0, Math.min(product.colorImages.length, 5)).map((color, index) => (
                          <div
                            key={index}
                            className="color-dot"
                            style={{
                              backgroundColor: getColorHex(color),
                              border: getColorHex(color) === '#FFFFFF' || getColorHex(color) === '#F8F8F8' ? '1.5px solid #ddd' : '1.5px solid rgba(0, 0, 0, 0.15)'
                            }}
                            title={color}
                          />
                        ))}
                        {product.colorImages.length > 5 && (
                          <span style={{ fontSize: '0.75rem', color: '#666' }}>+{product.colorImages.length - 5}</span>
                        )}
                      </div>
                    )}

                    <div className="product-specs">
                      {product.ram && <span className="spec">RAM: {product.ram}</span>}
                      {product.storage && <span className="spec">Storage: {product.storage}</span>}
                      {product.size && <span className="spec">Size: {product.size}</span>}
                      {product.channels && <span className="spec">{product.channels}</span>}
                      {product.type && <span className="spec">{product.type}</span>}
                    </div>

                    <div className="product-price-section">
                      <div className="price-info">
                        <p className="current-price">PKR {product.price.toLocaleString()}</p>
                        {product.discount > 0 && (
                          <p className="original-price">PKR {product.originalPrice.toLocaleString()}</p>
                        )}
                      </div>
                    </div>

                    <button className="btn-add-to-cart" onClick={(e) => {
                      e.stopPropagation();
                      handleWhatsAppOrder(product);
                    }}>
                      Shop Now
                    </button>
                  </div>
                    );
                  })}
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

export default Products;
